import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';

import {
  liquidCoolingCduCloneForCreate,
  liquidCoolingCduCreateDraft,
  liquidCoolingCduEnsureNameForCreate,
} from './subsections/liquidCoolingCduEngineBindings';
import { LiquidCoolingCduEngineEditFormBody } from './subsections/LiquidCoolingCduEngineEditFormBody';
import {
  isLiquidCoolingPersistedProps,
  type LiquidCoolingProps,
  type LiquidCoolingPersistedProps,
  type LiquidCoolingInitialProps,
} from './useLiquidCooling';
import { Eye, EyeOff } from '../../../components/icons';
import { buildLegacyLiquidCoolingCduFromRequestFields } from '../../../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { StepShell } from '../../../components/StepShell';
import { useLiquidCoolingCduConfigurations } from '../../../hooks/queries/useLiquidCoolingCduConfigurations';
import {
  descriptorInstanceToLiquidCoolingCdu,
  liquidCoolingCduToDescriptorInstance,
} from '../../../services/descriptorEditSessionFlush';
import {
  DescriptorEngineSection,
  type DescriptorInstance,
} from '../../sections/descriptorEngineAdapter';
import { LIQUID_COOLING_DESCRIPTOR } from '../../sections/liquidCooling.descriptor';

import type { LiquidCoolingCduConfigurationsSaveHandle } from './subsections/liquidCoolingCduConfigurationsSaveHandle';
import type { RepeatableInstanceEditFormRenderProps } from '../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { LiquidCoolingCduConfiguration } from '../../../types/liquidCoolingCdu';

const LiquidCoolingInitialLayout: React.FC<LiquidCoolingInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  showFullDetails = true,
  registerLiquidCoolingCduSaveHandle,
  onLiquidCoolingCduEditorActiveChange,
}) => {
  const stage = String(formData.stage ?? formData.sysRequestStage ?? 'DRAFT');

  // #997 Story 6.8 — the create/draft layout runs on the descriptor engine
  // (mirror Cooling 6.6). The engine owns the list/edit-session/save/flush/
  // attachment lifecycle (6.1 per-unit scoping + 6.2 temp→real remap now run
  // live); Liquid Cooling supplies its bespoke view
  // (`LiquidCoolingCduEngineEditFormBody` → `LiquidCoolingCduEditFormContent`, the
  // per-CDU details carousel + the flat Notes card) and its proven
  // draft/name/clone semantics (`liquidCoolingCduEngineBindings`) through the
  // engine's per-section seams, so the user-visible editor is unchanged. The
  // draft list stays parent-owned in `formData.liquidCoolingCduConfigurations`.
  const draftLiquidCoolingCdus =
    (formData.liquidCoolingCduConfigurations as
      | LiquidCoolingCduConfiguration[]
      | undefined) ?? [];
  const draftInstances = useMemo(
    () =>
      draftLiquidCoolingCdus.map(
        unit =>
          liquidCoolingCduToDescriptorInstance(unit) as DescriptorInstance,
      ),
    [draftLiquidCoolingCdus],
  );
  const requestIdNum = requestId ? Number.parseInt(requestId, 10) || 0 : 0;
  const handleDraftInstancesChange = useCallback(
    (instances: DescriptorInstance[]) =>
      updateFormData(
        'liquidCoolingCduConfigurations',
        instances.map(instance =>
          descriptorInstanceToLiquidCoolingCdu(
            instance,
            requestIdNum,
            LIQUID_COOLING_DESCRIPTOR,
          ),
        ),
      ),
    [updateFormData, requestIdNum],
  );

  // #997 Story 6.8 — the shared request-level field `lcLiquidToChipDesired`
  // renders inside the CDU editor but writes to the request (parent-owned
  // formData on the create path), not the CDU row.
  const handleSharedRequestFieldChange = useCallback(
    (field: string, value: string | boolean) => {
      updateFormData(field, value);
    },
    [updateFormData],
  );

  // Engine handle → bespoke LiquidCoolingCduConfigurationsSaveHandle
  // (CreateRequest's footer-save/dismiss/scope-removal bridge keeps its contract).
  const registerInstanceSaveHandle = useMemo(() => {
    if (!registerLiquidCoolingCduSaveHandle) {
      return undefined;
    }
    return (handle: RepeatableInstanceSaveHandle | null) => {
      if (!handle) {
        registerLiquidCoolingCduSaveHandle(null);
        return;
      }
      const liquidCoolingHandle: LiquidCoolingCduConfigurationsSaveHandle = {
        isLiquidCoolingCduEditorOpen: handle.isInstanceEditorOpen,
        hasUnsavedLiquidCoolingCduEditor: handle.hasUnsavedInstanceEditor,
        commitOpenLiquidCoolingCduEditor: handle.commitOpenInstanceEditor,
        saveUnsavedLiquidCoolingCduIfNeeded: async () => {
          if (
            !handle.isInstanceEditorOpen() ||
            !handle.hasUnsavedInstanceEditor()
          ) {
            return true;
          }
          return handle.commitOpenInstanceEditor();
        },
        hasPendingLiquidCoolingCduChanges: () =>
          handle.hasPendingListChanges?.() ?? false,
        flushPendingLiquidCoolingCduChanges: async () => {
          await handle.flushPendingListChanges?.();
        },
        tryDismissLiquidCoolingCduEditor: handle.tryDismissInstanceEditor,
        discardOpenLiquidCoolingCduEditor: handle.discardOpenInstanceEditor,
        // Draft list is parent-owned (formData.liquidCoolingCduConfigurations) —
        // scope removal strips it there; the in-memory session reset is the
        // persisted-path concern.
        resetSessionAfterScopeRemoval: () => {},
      };
      registerLiquidCoolingCduSaveHandle(liquidCoolingHandle);
    };
  }, [registerLiquidCoolingCduSaveHandle]);

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <LiquidCoolingCduEngineEditFormBody
        {...props}
        showAdditionalInformation
        sharedRequestFields={formData}
        onSharedRequestFieldChange={handleSharedRequestFieldChange}
      />
    ),
    [formData, handleSharedRequestFieldChange],
  );

  return (
    <StepShell
      title="Liquid Cooling"
      testID="create-form-header-liquid-cooling"
      
    >
      <DescriptorEngineSection
        descriptor={LIQUID_COOLING_DESCRIPTOR}
        requestId={requestId}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        requestStage={stage}
        draftInstances={draftInstances}
        onDraftInstancesChange={handleDraftInstancesChange}
        legacyRequestFields={formData}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        showFullDetails={showFullDetails}
        editBody={editBody}
        createDraft={liquidCoolingCduCreateDraft}
        ensureNameForCreate={liquidCoolingCduEnsureNameForCreate}
        cloneForCreate={liquidCoolingCduCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onLiquidCoolingCduEditorActiveChange}
        testID="liquid-cooling-cdu-configurations"
      />
    </StepShell>
  );
};

const LiquidCoolingPersistedLayout: React.FC<LiquidCoolingPersistedProps> = ({
  request,
  isEditing,
  editedData,
  updateEditedData,
  showFullDetails = true,
  attachmentsBySection = {},
  selectedFiles = {},
  onFilesSelected,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
  onAttachFiles,
  onDeleteAttachment,
  registerInstanceSaveHandle,
  applyEditedDataPatch,
  liquidCoolingCduConfigurationsDirty = false,
  onLiquidCoolingCduEditorActiveChange,
}) => {
  const requestIdStr = String(request.id ?? '');
  const fieldDisabled = !isEditing;
  const stage = String(request.stage || request.sysRequestStage || 'DRAFT');
  const isOfflineRequest =
    request.id != null && String(request.id).startsWith('o-');

  const pdfFormData = useMemo(
    () => ({
      ...(request as unknown as Record<string, unknown>),
      ...(isEditing ? editedData : {}),
    }),
    [request, editedData, isEditing],
  );

  // #997 Story 6.8 — the persisted layout runs on the descriptor engine (mirror
  // Cooling). Host-boundary display-list resolution: while the user has Liquid
  // Cooling edits this session, `editedData.liquidCoolingCduConfigurations` is
  // authoritative (even when empty — deleted-all must not resurrect); otherwise
  // units query > request embed > legacy flat-unit synthesis.
  const { data: queryUnits = [] } = useLiquidCoolingCduConfigurations(
    requestIdStr,
    true,
  );
  const editedUnits = editedData?.liquidCoolingCduConfigurations;
  const effectiveUnits = useMemo((): LiquidCoolingCduConfiguration[] => {
    if (
      isEditing &&
      liquidCoolingCduConfigurationsDirty &&
      Array.isArray(editedUnits)
    ) {
      return editedUnits as LiquidCoolingCduConfiguration[];
    }
    if (queryUnits.length > 0) {
      return queryUnits;
    }
    const embed = pdfFormData.liquidCoolingCduConfigurations;
    if (Array.isArray(embed) && embed.length > 0) {
      return embed as LiquidCoolingCduConfiguration[];
    }
    const legacyUnit = buildLegacyLiquidCoolingCduFromRequestFields(
      pdfFormData,
      requestIdStr,
    );
    return legacyUnit ? [legacyUnit] : [];
  }, [
    isEditing,
    liquidCoolingCduConfigurationsDirty,
    editedUnits,
    queryUnits,
    pdfFormData,
    requestIdStr,
  ]);

  const legacyRequestFields = useMemo(
    () => ({ ...pdfFormData, liquidCoolingCduConfigurations: effectiveUnits }),
    [pdfFormData, effectiveUnits],
  );

  // Shared request-level field `lcLiquidToChipDesired`: routes to editedData when
  // editing so it persists on the request PATCH (unchanged from the pre-swap path).
  const handleSharedRequestFieldChange = useCallback(
    (field: string, value: string | boolean) => {
      if (isEditing) {
        updateEditedData(field, value);
      }
    },
    [isEditing, updateEditedData],
  );

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <LiquidCoolingCduEngineEditFormBody
        {...props}
        showAdditionalInformation
        showFullDetails={showFullDetails}
        sharedRequestFields={legacyRequestFields}
        onSharedRequestFieldChange={handleSharedRequestFieldChange}
      />
    ),
    [showFullDetails, legacyRequestFields, handleSharedRequestFieldChange],
  );

  return (
    <View style={liquidCoolingPersistedStyles.container}>
      <PersistedStepHeader
        title="Liquid Cooling"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={liquidCoolingPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={liquidCoolingPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      <DescriptorEngineSection
        descriptor={LIQUID_COOLING_DESCRIPTOR}
        requestId={requestIdStr}
        isEditing={isEditing}
        // #997 QA-C: keep view-mode rows drillable; mutations stay gated by
        // isEditing/listReadOnly, so do NOT restore fieldDisabled (=!isEditing) here.
        disabled={false}
        requestStage={stage}
        showFullDetails={showFullDetails}
        legacyRequestFields={legacyRequestFields}
        applyEditedDataPatch={applyEditedDataPatch}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={isOfflineRequest || offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        editBody={editBody}
        createDraft={liquidCoolingCduCreateDraft}
        ensureNameForCreate={liquidCoolingCduEnsureNameForCreate}
        cloneForCreate={liquidCoolingCduCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onLiquidCoolingCduEditorActiveChange}
        testID="liquid-cooling-cdu-configurations"
      />
    </View>
  );
};

const liquidCoolingPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
});

export const LiquidCooling: React.FC<LiquidCoolingProps> = props =>
  isLiquidCoolingPersistedProps(props) ? (
    <LiquidCoolingPersistedLayout {...props} />
  ) : (
    <LiquidCoolingInitialLayout {...props} />
  );

LiquidCooling.displayName = 'LiquidCooling';

/** @deprecated Test / caption suites — compose path only */
export const LiquidCoolingStep = LiquidCoolingInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const LiquidCoolingInRequest = LiquidCoolingPersistedLayout;
